import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { versionDependent } from '../../images/versionDependentExport';
import { LayoutExtension } from '../../types/ResponsiveState';
import { Link, LinkTarget, LinkType } from '../Link/Link';
import { FacebookCircle as FacebookCircleIcon } from '../icons/FacebookCircle/FacebookCircle';
import {
  FacebookCircleV2 as FacebookCircleIconV2,
} from '../icons/FacebookCircleV2/FacebookCircleV2';
import { InstagramCircle as InstagramCircleIcon } from '../icons/InstagramCircle/InstagramCircle';
import {
  InstagramCircleV2 as InstagramCircleIconV2,
} from '../icons/InstagramCircleV2/InstagramCircleV2';
import { LinkedInCircle as LinkedInCircleIcon } from '../icons/LinkedInCircle/LinkedInCircle';
import { PinterestCircle as PinterestCircleIcon } from '../icons/PinterestCircle/PinterestCircle';
import { TiktokCircle as TiktokCircleIcon } from '../icons/TiktokCircle/TiktokCircle';
import { TiktokCircleV2 as TiktokCircleIconV2 } from '../icons/TiktokCircleV2/TiktokCircleV2';
import { TwitterCircle as TwitterCircleIcon } from '../icons/TwitterCircle/TwitterCircle';
import { YoutubeCircle as YoutubeCircleIcon } from '../icons/YoutubeCircle/YoutubeCircle';
import styles from './FooterBottomPartSocialIcons.module.css';

const messages = defineMessages({
  facebookLink: {
    id: 'src.components.FooterBottomPartSocialIcons.facebookLink',
    defaultMessage: 'https://www.facebook.com/cadeaubon.nl/',
  },
  twitterLink: {
    id: 'src.components.FooterBottomPartSocialIcons.twitterLink',
    defaultMessage: 'https://twitter.com/',
  },
  instagramLink: {
    id: 'src.components.FooterBottomPartSocialIcons.instagramLink',
    defaultMessage: 'https://www.instagram.com/cadeaubon.nl/',
  },
  youtubeLink: {
    id: 'src.components.FooterBottomPartSocialIcons.youtubeLink',
    defaultMessage: 'https://www.youtube.com/',
  },
  tiktokLink: {
    id: 'src.components.FooterBottomPartSocialIcons.tiktokLink',
    defaultMessage: 'https://www.tiktok.com/@cadeaubonnl',
  },
  linkedInLink: {
    id: 'src.components.FooterBottomPartSocialIcons.linkedInLink',
    defaultMessage: 'https://www.linkedin.com/company/cadeaubon-nl/',
  },
  pinterestLink: {
    id: 'src.components.FooterBottomPartSocialIcons.pinterestLink',
    defaultMessage: 'https://nl.pinterest.com/cadeaubon_NL/',
  },
});

export enum SocialIcon {
  facebook = 'facebook',
  facebookV2 = 'facebookV2',
  instagram = 'instagram',
  instagramV2 = 'instagramV2',
  linkedIn = 'linkedIn',
  pinterest = 'pinterest',
  tiktok = 'tiktok',
  tiktokV2 = 'tiktokV2',
  twitter = 'twitter',
  youtube = 'youtube',
}

type SocialIconData = {
  icon: JSX.Element;
  isHidden: boolean;
  message: { defaultMessage: string; id: string };
};

export const MOBILE_ICON_SIZE = 36;

export const hooks = {
  useGetSocialIconsSize(layoutExtension: LayoutExtension) {
    return React.useMemo(() => {
      return layoutExtension === LayoutExtension.Mobile ? MOBILE_ICON_SIZE : undefined;
    }, [layoutExtension]);
  },
};

export type Props = {
  layoutExtension: LayoutExtension;
};

export const FooterBottomPartSocialIcons = React.memo((props: Props) => {
  const { layoutExtension } = props;
  const intl = useIntl();

  const socialIconsSize = hooks.useGetSocialIconsSize(layoutExtension);

  const { isClient } = hydrationHooks.useIsClient();

  const socialIcons: {[key in SocialIcon]: SocialIconData } = React.useMemo(() => {
    return {
      facebook: {
        message: messages.facebookLink,
        icon: <FacebookCircleIcon />,
        isHidden: false,
      },
      facebookV2: {
        message: messages.facebookLink,
        icon: <FacebookCircleIconV2 height={socialIconsSize} width={socialIconsSize} />,
        isHidden: false,
      },
      twitter: {
        message: messages.twitterLink,
        icon: <TwitterCircleIcon />,
        isHidden: true,
      },
      instagram: {
        message: messages.instagramLink,
        icon: <InstagramCircleIcon />,
        isHidden: false,
      },
      instagramV2: {
        message: messages.instagramLink,
        icon: <InstagramCircleIconV2 height={socialIconsSize} width={socialIconsSize} />,
        isHidden: false,
      },
      youtube: {
        message: messages.youtubeLink,
        icon: <YoutubeCircleIcon />,
        isHidden: true,
      },
      tiktok: {
        message: messages.tiktokLink,
        icon: <TiktokCircleIcon />,
        isHidden: false,
      },
      tiktokV2: {
        message: messages.tiktokLink,
        icon: <TiktokCircleIconV2 height={socialIconsSize} width={socialIconsSize} />,
        isHidden: false,
      },
      linkedIn: {
        message: messages.linkedInLink,
        icon: <LinkedInCircleIcon height={socialIconsSize} width={socialIconsSize} />,
        isHidden: false,
      },
      pinterest: {
        message: messages.pinterestLink,
        icon: <PinterestCircleIcon height={socialIconsSize} width={socialIconsSize} />,
        isHidden: false,
      } };
  },[socialIconsSize]);

  return (
    <div className={styles.footerBottomPartSocialIcons}>
      {isClient && versionDependent.socialLinks.map(link => {
        const socialIcon = socialIcons[link];
        const socialIconUrl = intl.formatMessage(socialIcon.message);

        return (<Link
          key={link}
          type={LinkType.ExternalHref}
          to={socialIconUrl}
          className={socialIcon.isHidden
            ? classNames(styles.icon, styles.hiddenLink)
            : styles.icon
          }
          target={LinkTarget.Blank}
          children={socialIcon.icon}
        />);
      })}
    </div>
  );
});

FooterBottomPartSocialIcons.displayName = 'FooterBottomPartSocialIcons';
